/**
 * Layout wrapper for project pages.
 * Provides the shared header: org and title, favorite, settings, search, members.
 * Note: This is nested inside AppLayout which provides the sidebar.
 */

import { useState, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, X, MoreHorizontal, Settings, Lock, Star } from 'lucide-react';
import { SearchDropdown } from '@/components/ui/SearchDropdown';
import { orgOf } from '@/lib/project-groups';
import { EventModal } from '@/components/modals/EventModal';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { ManageMembersModal } from '@/components/modals/ManageMembersModal';
import { EditProjectModal } from '@/components/modals/EditProjectModal';
import { AnnouncementBanner } from '@/components/ui/AnnouncementBanner';
import { Avatar } from '@/components/ui/Avatar';
import { useSocketStore } from '@/stores/socket.store';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { useCanFavorite } from '@/hooks/useCanFavorite';
import { projectsApi, tasksApi, announcementsApi, CalendarEvent, CalendarTask } from '@/lib/api';
import { cn } from '@/lib/utils';

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventInitialDate, setEventInitialDate] = useState<Date | undefined>(undefined);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [selectedTask, setSelectedTask] = useState<CalendarTask | null>(null);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { user } = useAuthStore();
  const canFavorite = useCanFavorite();

  // Get project details
  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  // Fetch task lists for this project (needed for CreateTaskModal)
  const { data: taskLists } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId!),
    enabled: !!projectId,
  });

  // Fetch active announcements for this project
  const { data: announcements } = useQuery({
    queryKey: ['announcements', 'project', projectId],
    queryFn: () => announcementsApi.getActiveForProject(projectId!),
    enabled: !!projectId,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Favorite/unfavorite mutation
  const queryClient = useQueryClient();
  const favoriteMutation = useMutation({
    mutationFn: (isFavorited: boolean) =>
      isFavorited ? projectsApi.unfavorite(projectId!) : projectsApi.favorite(projectId!),
    onMutate: async (isFavorited: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['project', projectId] });
      const previous = queryClient.getQueryData<any>(['project', projectId]);
      if (previous) {
        queryClient.setQueryData(['project', projectId], { ...previous, isFavorited: !isFavorited });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['project', projectId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  // Get socket subscription methods
  const { subscribeToProject, unsubscribeFromProject } = useSocketStore();

  // Subscribe to project room for real-time updates (e.g., AI summary updates)
  useEffect(() => {
    if (projectId) {
      subscribeToProject(projectId);
      return () => {
        unsubscribeFromProject(projectId);
      };
    }
  }, [projectId, subscribeToProject, unsubscribeFromProject]);

  // Handlers for calendar events
  const handleAddEvent = (date?: Date) => {
    setEventInitialDate(date);
    setEditingEvent(null);
    setShowEventModal(true);
  };

  // Staff / admin of THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember, isProjectAdmin: isAgencyAdmin } = useProjectRoles(projectId);

  // Check if user can edit a specific event
  // Agency admins can edit any event, others can only edit events they created
  const canEditEvent = (event: CalendarEvent) => {
    if (isAgencyAdmin) return true;
    return event.createdBy?.id === user?.id;
  };

  const handleEventClick = (event: CalendarEvent) => {
    // Only open modal if user has permission
    if (canEditEvent(event)) {
      setEditingEvent(event);
      setEventInitialDate(undefined);
      setShowEventModal(true);
    }
  };

  const handleTaskClick = (task: CalendarTask) => {
    setSelectedTask(task);
  };

  const isArchived = !!project?.archivedAt;
  const memberCount = project?.members?.length || 0;

  // Context to pass to child routes
  const outletContext = {
    project,
    onAddEvent: !isArchived ? handleAddEvent : undefined,
    onEventClick: handleEventClick,
    onTaskClick: handleTaskClick,
    canEditEvent: !isArchived ? canEditEvent : () => false,
    isAgencyMember,
    isAgencyAdmin,
    userId: user?.id,
  };

  // The project's screens are in the left nav (ProjectNav), not tabs here
  const orgName = project ? orgOf(project).name : '';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Project Header - white background covers top of content area */}
      {/* z-20, deliberately BELOW the AppShell topbar's z-30: both are sticky,
          and at equal z the later DOM node wins — which put this header on top
          of the global search dropdown (a child of the topbar's context, so
          its own z-50 could never escape). The page has no z-20+ content, so
          this header still beats everything it should. */}
      <header className="sticky top-14 lg:top-0 z-20 bg-white border-b border-grey-200">
        {/* Dark top strip: breadcrumb + actions + members */}
        <div className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          {/* Header row: the project's name (its org beneath), favorite,
              settings, search and members. Switching projects happens in the
              nav's dropdowns. */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="min-w-0">
              {orgName && (
                <p className="truncate text-caption font-semibold uppercase tracking-wider text-grey-400">{orgName}</p>
              )}
              <h1 className="text-heading-3 text-secondary truncate min-w-0">
                {project?.name || (isProjectLoading ? 'Loading...' : 'Project Not Found')}
              </h1>
            </div>
            {project?.isPrivate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-600 border border-grey-200 shrink-0">
                <Lock className="w-3 h-3" />
                Private
              </span>
            )}
            {isArchived && (
              <span className="badge badge-grey text-xs shrink-0">Archived</span>
            )}
            {/* Favorite toggle — agency people only (useCanFavorite) */}
            {project && canFavorite && (
              <button
                onClick={() => favoriteMutation.mutate(!!project.isFavorited)}
                className={cn(
                  'p-1.5 rounded-lg transition-colors shrink-0',
                  project.isFavorited
                    ? 'text-warning hover:bg-grey-100'
                    : 'text-grey-400 hover:bg-grey-100 hover:text-warning'
                )}
                title={project.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star className={cn('w-4 h-4', project.isFavorited && 'fill-yellow-400')} />
              </button>
            )}
            {/* Edit button */}
            {isAgencyAdmin && project && (
              <button
                onClick={() => setShowEditModal(true)}
                className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-secondary transition-colors shrink-0"
                title="Project settings"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}

            {/* Project search — desktop; the topbar's global search covers phones */}
            <div className="relative ml-auto hidden w-72 md:block">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400 group-focus-within:text-grey-600 transition-colors" />
                <input
                  type="text"
                  aria-label={`Search ${project?.name ?? 'this project'}`}
                  placeholder="Search this project…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(e.target.value.length >= 2);
                  }}
                  onFocus={() => {
                    if (searchQuery.length >= 2) {
                      setSearchOpen(true);
                    }
                  }}
                  className="w-full h-9 pl-9 pr-9 text-sm bg-grey-100 border-0 rounded-lg hover:bg-grey-150 focus:bg-white focus:ring-2 focus:ring-grey-200 transition-all placeholder:text-grey-400"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchOpen(false);
                    }}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-grey-400 hover:text-grey-600 hover:bg-grey-200 rounded transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <SearchDropdown
                query={searchQuery}
                isOpen={searchOpen}
                onClose={() => setSearchOpen(false)}
                projectId={projectId}
                organizationId={project?.organizationId}
              />
            </div>

            {/* Members - inline, right-aligned, tighter overlap on mobile */}
            <div className="ml-auto md:ml-0 flex items-center shrink-0">
              {/* Avatar stack — tighter overlap on mobile */}
              <div className="flex -space-x-3 sm:-space-x-2">
                {project?.members?.slice(0, 4).map((member: any) => (
                  <Avatar
                    key={member.userId}
                    src={member.user?.avatarUrl}
                    name={member.user?.name || 'User'}
                    size="sm"
                    className="ring-2 ring-white"
                  />
                ))}
              </div>

              {/* Overflow indicator */}
              {memberCount > 4 && (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-grey-200 flex items-center justify-center text-xs text-grey-600 font-medium -ml-3 sm:-ml-2 ring-2 ring-white">
                  +{memberCount - 4}
                </div>
              )}

              {/* "..." button */}
              <button
                onClick={() => setShowManageMembers(true)}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-grey-100 hover:bg-grey-200 flex items-center justify-center ml-0.5 sm:ml-1 ring-2 ring-white transition-colors"
                title={isAgencyAdmin ? 'Manage members' : 'View members'}
              >
                <MoreHorizontal className="w-4 h-4 text-grey-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Announcements banner, under the title */}
        {announcements && announcements.length > 0 && (
          <div className="px-4 sm:px-6 lg:px-8 pb-3">
            <AnnouncementBanner announcements={announcements} />
          </div>
        )}
      </header>

      {/* Main content with grey background */}
      <main className="flex-1 bg-grey-100 py-6 px-4 sm:px-6 lg:px-8">
        <div className="animate-fade-in">
          <Outlet context={outletContext} />
        </div>
      </main>

      {/* Event Modal */}
      {projectId && (
        <EventModal
          isOpen={showEventModal}
          projectId={projectId}
          onClose={() => {
            setShowEventModal(false);
            setEventInitialDate(undefined);
            setEditingEvent(null);
          }}
          event={editingEvent}
          initialDate={eventInitialDate}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && projectId && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          task={{
            id: selectedTask.id,
            title: selectedTask.title,
            description: selectedTask.description,
            status: selectedTask.status,
            priority: selectedTask.priority,
            dueDate: selectedTask.dueDate,
            assignee: selectedTask.assignee,
            assigneeId: selectedTask.assigneeId,
            createdById: selectedTask.createdById,
            createdBy: selectedTask.createdBy,
          }}
          projectId={projectId}
          listId={selectedTask.list.id}
          organizationId={project.organizationId}
        />
      )}

      {/* Manage Members Modal */}
      {project && (
        <ManageMembersModal
          isOpen={showManageMembers}
          onClose={() => setShowManageMembers(false)}
          projectId={projectId!}
          project={project}
          readOnly={!isAgencyAdmin}
        />
      )}

      {/* Edit Project Modal */}
      {project && (
        <EditProjectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          project={project}
        />
      )}
    </div>
  );
}
